// src/components/GamificationOverlay.jsx — Real-Time Gamification Overlays
// Coins gains · Level ups · Badge earned · Daily claim popups
// Listens to gamification WebSocket events and shows animated overlays
import { useState, useEffect } from 'react';
import { getSocket } from '../hooks/useSocket';
import { G } from '../constants';

const LEVEL_TITLES = ['Rookie','Apprentice','Trader','Strategist','Analyst','Expert','Master','Elite','Legend','Grandmaster','Titan','Immortal'];

function lvColor(lv) {
  if (lv >= 10) return '#FFD700';
  if (lv >= 7)  return '#B26BFF';
  if (lv >= 5)  return '#0ECB81';
  if (lv >= 3)  return '#4B8BF5';
  return '#888';
}

// ── Coins float (small +Coins bubble) ──────────────────────────────────────────────
function CoinFloat({ item, onRemove }) {
  useEffect(() => {
    const t = setTimeout(onRemove, 2200);
    return () => clearTimeout(t);
  }, [onRemove]);

  return (
    <div style={{
      background:`linear-gradient(135deg,#0D0A00,#1A1200)`,
      border:`1px solid ${G}88`,
      borderRadius:20, padding:'6px 14px',
      display:'flex', alignItems:'center', gap:6,
      animation:'xpFloat 2.2s ease forwards',
      pointerEvents:'none',
      boxShadow:`0 4px 16px ${G}44`,
    }}>
      <span style={{ fontSize:14 }}>⭐</span>
      <span style={{ color:G, fontWeight:900, fontSize:14 }}>
        +{item.delta} 🪙 Coins
      </span>
      {item.level && (
        <span style={{ color:'#555', fontSize:10 }}>
          Lv{item.level}
        </span>
      )}
    </div>
  );
}

// ── Level Up Modal ────────────────────────────────────────────────────────────
function LevelUpModal({ data, onClose }) {
  const col = lvColor(data.newLevel);

  // S-013 Item 8: 5000ms → 3500ms (Sandeep wants 3-4 sec auto-dismiss)
  // Cleanup function ensures timer is cleared on unmount, preventing stuck overlay.
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <div style={{
      position:'fixed', top:'50%', left:'50%', transform:'translate(-50%,-50%)',
      zIndex:10000, textAlign:'center', pointerEvents:'all',
      animation:'lvUpAnim 0.6s ease forwards',
    }} onClick={onClose}>
      {/* Glow background */}
      <div style={{
        position:'absolute', inset:-40, borderRadius:'50%',
        background:col, opacity:0.08, filter:'blur(30px)',
      }}/>

      <div style={{
        position:'relative',
        background:'linear-gradient(135deg,#0D0A00,#1A1200)',
        border:`2px solid ${col}`,
        borderRadius:20, padding:'28px 36px',
        boxShadow:`0 8px 40px ${col}55`,
        minWidth:240,
      }}>
        <div style={{ fontSize:52, marginBottom:8 }}>🎉</div>
        <div style={{ color:'#888', fontSize:12, marginBottom:4, letterSpacing:2 }}>
          LEVEL UP!
        </div>
        <div style={{
          width:70, height:70, borderRadius:'50%', margin:'0 auto 12px',
          background:`linear-gradient(135deg,${col}44,${col}22)`,
          border:`3px solid ${col}`,
          display:'flex', alignItems:'center', justifyContent:'center',
          fontSize:28, fontWeight:900, color:col,
          boxShadow:`0 0 20px ${col}66`,
        }}>
          {data.newLevel}
        </div>
        <div style={{ color:col, fontWeight:900, fontSize:22 }}>
          {data.title || LEVEL_TITLES[data.newLevel - 1]}
        </div>
        <div style={{ color:'#555', fontSize:11, marginTop:4 }}>
          {data.message}
        </div>
        <div style={{ color:'#333', fontSize:9, marginTop:12 }}>Tap to dismiss</div>
      </div>
    </div>
  );
}

// ── Badge Earned Notification ────────────────────────────────────────────────
function BadgeNotif({ data, onRemove }) {
  useEffect(() => {
    const t = setTimeout(onRemove, 4000);
    return () => clearTimeout(t);
  }, [onRemove]);

  return (
    <div style={{
      background:'linear-gradient(135deg,#0D0A00,#1A1200)',
      border:`1px solid ${G}66`,
      borderRadius:14, padding:'12px 16px',
      display:'flex', gap:10, alignItems:'center',
      animation:'badgeSlide 0.4s ease forwards',
      boxShadow:`0 6px 24px rgba(0,0,0,0.6)`,
      maxWidth:280, cursor:'pointer',
    }} onClick={onRemove}>
      <div style={{ fontSize:30 }}>{data.badge?.icon || '🏅'}</div>
      <div>
        <div style={{ color:G, fontWeight:800, fontSize:12 }}>New Badge Earned!</div>
        <div style={{ color:'#CCC', fontWeight:700, fontSize:13 }}>{data.badge?.name || 'Achievement'}</div>
        <div style={{ color:'#555', fontSize:10, marginTop:2 }}>+{data.badge?.coins_reward || 0} Coins awarded</div>
      </div>
    </div>
  );
}

// ── Daily Claim Notification ─────────────────────────────────────────────────
function DailyClaimNotif({ data, onRemove }) {
  useEffect(() => {
    const t = setTimeout(onRemove, 3500);
    return () => clearTimeout(t);
  }, [onRemove]);

  return (
    <div style={{
      background:'linear-gradient(135deg,#0D0A00,#1A1200)',
      border:`1px solid ${G}66`,
      borderRadius:14, padding:'12px 16px',
      display:'flex', gap:10, alignItems:'center',
      animation:'badgeSlide 0.4s ease forwards',
      boxShadow:`0 6px 24px rgba(0,0,0,0.6)`,
      maxWidth:280, cursor:'pointer',
    }} onClick={onRemove}>
      <div style={{ fontSize:28 }}>{data.reward?.icon || '🎁'}</div>
      <div>
        <div style={{ color:G, fontWeight:800, fontSize:12 }}>{data.reward?.label || 'Daily Reward!'}</div>
        <div style={{ color:'#CCC', fontSize:11, marginTop:2 }}>
          +🪙{(data.coins || 0) + (data.coins_delta || 0)} Coins
        </div>
      </div>
    </div>
  );
}

// ── Challenge Complete Notification ───────────────────────────────────────────
function ChallengeNotif({ data, onRemove }) {
  useEffect(() => {
    const t = setTimeout(onRemove, 3500);
    return () => clearTimeout(t);
  }, [onRemove]);

  return (
    <div style={{
      background:'linear-gradient(135deg,#0A3D26,#0D1A0D)',
      border:`1px solid #0ECB8166`,
      borderRadius:14, padding:'12px 16px',
      display:'flex', gap:10, alignItems:'center',
      animation:'badgeSlide 0.4s ease forwards',
      boxShadow:`0 6px 24px rgba(0,0,0,0.6)`,
      maxWidth:280, cursor:'pointer',
    }} onClick={onRemove}>
      <div style={{ fontSize:26 }}>{data.challenge?.icon || '🎯'}</div>
      <div>
        <div style={{ color:'#0ECB81', fontWeight:800, fontSize:12 }}>Challenge Complete!</div>
        <div style={{ color:'#CCC', fontSize:11 }}>{data.challenge?.title}</div>
        <div style={{ color:'#555', fontSize:10, marginTop:2 }}>+{data.coins_deltaAwarded} 🪙 Coins earned</div>
      </div>
    </div>
  );
}

// ── MAIN OVERLAY CONTAINER ────────────────────────────────────────────────────
let _notifCounter = 0;

export default function GamificationOverlay({ userId }) {
  const [coinFloats,   setXpFloats]   = useState([]);  // [{id, delta, level}]
  const [levelUp,    setLevelUp]    = useState(null); // { newLevel, title, message }
  const [notifQueue, setNotifQueue] = useState([]);   // [{id, type, data}]

  const addNotif = (type, data) => {
    const id = ++_notifCounter;
    setNotifQueue(prev => [...prev.slice(-3), { id, type, data }]);
    // v225: Safety auto-clear — force remove after 5 seconds max (prevents stuck toasts)
    setTimeout(() => removeNotif(id), 5000);
  };

  const removeNotif = (id) => {
    setNotifQueue(prev => prev.filter(n => n.id !== id));
  };

  const addCoinFloat = (delta, level) => {
    const id = ++_notifCounter;
    setXpFloats(prev => [...prev.slice(-4), { id, delta, level }]);
    setTimeout(() => setXpFloats(prev => prev.filter(x => x.id !== id)), 2200);
  };

  // ── WS listeners ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!userId) return;
    const socket = getSocket();

    const onCoinsGained = (d) => {
      if (d.delta > 0) addCoinFloat(d.delta, d.level);
    };

    const onLevelUp = (d) => {
      setLevelUp(d);
    };

    const onBadge = (d) => {
      addNotif('badge', d);
    };

    const onDailyClaimed = (d) => {
      addNotif('daily', d);
    };

    const onChallengeComplete = (d) => {
      addNotif('challenge', d);
    };

    socket.on('coins_gained',           onCoinsGained);
    socket.on('level_up',            onLevelUp);
    socket.on('badge_earned',        onBadge);
    socket.on('daily_claimed',       onDailyClaimed);
    socket.on('challenge_completed', onChallengeComplete);

    return () => {
      socket.off('coins_gained',           onCoinsGained);
      socket.off('level_up',            onLevelUp);
      socket.off('badge_earned',        onBadge);
      socket.off('daily_claimed',       onDailyClaimed);
      socket.off('challenge_completed', onChallengeComplete);
    };
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <style>{`
        @keyframes xpFloat {
          0%   { opacity:1; transform:translateY(0) scale(1); }
          70%  { opacity:1; transform:translateY(-40px) scale(1.05); }
          100% { opacity:0; transform:translateY(-60px) scale(0.9); }
        }
        @keyframes lvUpAnim {
          0%   { opacity:0; transform:translate(-50%,-50%) scale(0.5); }
          60%  { transform:translate(-50%,-50%) scale(1.05); }
          100% { opacity:1; transform:translate(-50%,-50%) scale(1); }
        }
        @keyframes badgeSlide {
          from { opacity:0; transform:translateX(100px); }
          to   { opacity:1; transform:translateX(0); }
        }
      `}</style>

      {/* Coins float bubbles — bottom center */}
      <div style={{
        position:'fixed', bottom:90, left:'50%', transform:'translateX(-50%)',
        zIndex:9998, display:'flex', flexDirection:'column', gap:6,
        alignItems:'center', pointerEvents:'none',
      }}>
        {coinFloats.map(cf => (
          <CoinFloat key={cf.id} item={cf} onRemove={() => {}}/>
        ))}
      </div>

      {/* Level up modal — center screen */}
      {levelUp && (
        <LevelUpModal data={levelUp} onClose={() => setLevelUp(null)}/>
      )}

      {/* Notification stack — top right — auto-dismiss + tap-to-dismiss */}
      <div style={{
        position:'fixed', top:70, right:12,
        zIndex:9997, display:'flex', flexDirection:'column', gap:8,
        alignItems:'flex-end', pointerEvents:'auto',
      }}>
        {notifQueue.map(n => {
          if (n.type === 'badge')     return <BadgeNotif    key={n.id} data={n.data} onRemove={() => removeNotif(n.id)}/>;
          if (n.type === 'daily')     return <DailyClaimNotif key={n.id} data={n.data} onRemove={() => removeNotif(n.id)}/>;
          if (n.type === 'challenge') return <ChallengeNotif key={n.id} data={n.data} onRemove={() => removeNotif(n.id)}/>;
          return null;
        })}
      </div>
    </>
  );
}
